import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Sparkle, ArrowLeft, CheckCircle, Warning, Copy, Download,
  ArrowRight,
} from '@phosphor-icons/react';
import api from '../lib/api';

/**
 * PUBLIC marketing-asset generator — 4 fields (product, what, audience,
 * differentiator) → full asset pack rendered inline (hero copy, email
 * subjects, tweet thread, LinkedIn post, ad variations, video script).
 *
 * Not gated behind login. Rate-limited to 3 per IP per 24h by the
 * backend so prospects can try it, get impressed, then convert.
 */
export default function MarketingGeneratorPage() {
  const [form, setForm] = useState({
    product_name: '',
    description: '',
    audience: '',
    differentiator: '',
    tone: 'confident, punchy, plain-spoken',
    company_website: '', // honeypot
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const canSubmit =
    form.product_name.trim().length >= 2 &&
    form.description.trim().length >= 10 &&
    form.audience.trim().length >= 3 &&
    form.differentiator.trim().length >= 5 &&
    !loading;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.post('/ai/marketing-generate', form);
      setResult(res.data);
      // Analytics — this is a valuable Reddit/Google Ads conversion signal
      try {
        if (typeof window.rdt === 'function') {
          window.rdt('track', 'Lead', { customEventName: 'marketing_generator_used' });
        }
        if (typeof window.posthog !== 'undefined') {
          window.posthog.capture('marketing_generator_completed', {
            product_name: form.product_name,
          });
        }
      } catch (_) { /* analytics failure — no-op */ }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        setError(detail || "You've used all 3 free generations today. Sign up for unlimited.");
      } else if (status === 422) {
        setError('Please fill in all four fields with a bit more detail.');
      } else {
        setError(detail || 'Crunchy hit a snag. Please try again in a moment.');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch (_) {
      toast.error('Copy failed — please select and copy manually');
    }
  };

  const downloadAll = () => {
    if (!result?.assets) return;
    const a = result.assets;
    const md = [
      `# Marketing Asset Pack — ${result.product_name}`,
      `Generated ${new Date(result.generated_at).toLocaleString()} by Crunchy · SalesCrunchCRM`,
      '',
      '## Landing page hero',
      `**Eyebrow:** ${a.hero?.eyebrow || ''}`,
      `**Headline:** ${a.hero?.headline || ''}`,
      `**Subheadline:** ${a.hero?.subheadline || ''}`,
      `**Primary CTA:** ${a.hero?.cta_primary || ''}`,
      `**Secondary CTA:** ${a.hero?.cta_secondary || ''}`,
      '',
      '## Email subject lines',
      ...(a.email_subject_lines || []).map((s, i) => `${i + 1}. ${s}`),
      '',
      '## Twitter / X thread',
      ...(a.tweet_thread || []).map((t, i) => `**${i + 1}/${a.tweet_thread.length}** — ${t}`),
      '',
      '## LinkedIn post',
      a.linkedin_post || '',
      '',
      '## Meta ad variations',
      ...(a.meta_ads || []).map(
        (ad, i) => `**Ad ${i + 1}** — Headline: ${ad.headline}\nPrimary text: ${ad.primary_text}`
      ),
      '',
      '## 30-second video script',
      a.video_script_30s || '',
      '',
      '---',
      'Generated by [Crunchy](https://salescrunchcrm.com) — the AI concierge that lives inside SalesCrunchCRM.',
    ].join('\n');

    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${result.product_name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-marketing-pack.md`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Marketing pack downloaded');
  };

  return (
    <div className="min-h-screen bg-white text-[#0F0F1A] pb-32" data-testid="marketing-generator-page">
      {/* Header */}
      <header className="border-b border-black/5 sticky top-0 bg-white/95 backdrop-blur z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-black tracking-tight" data-testid="mktgen-brand-link">
            SalesCrunch<span className="text-[#F39C12]">CRM</span>
          </Link>
          <Link to="/" className="text-sm text-[#6B7280] hover:text-black flex items-center gap-1">
            <ArrowLeft size={14} weight="bold" />
            Back to home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 py-14 sm:py-20 border-b border-black/5">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 mb-5 text-xs font-bold uppercase tracking-widest text-amber-900">
            <Sparkle size={12} weight="fill" className="text-[#F39C12]" />
            Crunchy AI · Free to try
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-4 leading-[1.05]">
            Describe your product in 4 lines.<br />
            <span className="text-[#F39C12]">Get a full marketing pack.</span>
          </h1>
          <p className="text-lg text-[#374151] leading-relaxed max-w-2xl mx-auto">
            Landing hero. 3 email subject lines. A 6-tweet thread. LinkedIn
            post. 3 Meta ad variations. And a 30-second video script.
            Generated by Crunchy in ~15 seconds.
          </p>
          <p className="mt-4 text-xs text-[#9CA3AF]">
            No signup. 3 free generations per day. Same AI concierge that
            lives inside SalesCrunchCRM.
          </p>
        </div>
      </section>

      {/* Form + Output */}
      <section className="px-6 py-12">
        <div className="max-w-3xl mx-auto">
          {!result && (
            <form
              onSubmit={onSubmit}
              className="bg-white border border-black/10 rounded-lg p-6 sm:p-8 shadow-sm"
              data-testid="mktgen-form"
            >
              <div className="space-y-5">
                <Field
                  label="Product name"
                  hint="What is it called?"
                  value={form.product_name}
                  onChange={(v) => setForm({ ...form, product_name: v })}
                  placeholder="e.g. SalesCrunchCRM"
                  testid="mktgen-product-name"
                />
                <TextareaField
                  label="What does it do?"
                  hint="One or two sentences. Plain English."
                  value={form.description}
                  onChange={(v) => setForm({ ...form, description: v })}
                  placeholder="e.g. A CRM built for people who miss ACT! 4.0 — three-column contact card, Boolean lookup, no learning curve."
                  testid="mktgen-description"
                />
                <Field
                  label="Who is it for?"
                  hint="Be specific. Not 'everyone.'"
                  value={form.audience}
                  onChange={(v) => setForm({ ...form, audience: v })}
                  placeholder="e.g. Financial advisors and insurance agents who left ACT!"
                  testid="mktgen-audience"
                />
                <TextareaField
                  label="What makes it different?"
                  hint="The one thing competitors can't say."
                  value={form.differentiator}
                  onChange={(v) => setForm({ ...form, differentiator: v })}
                  placeholder="e.g. It's the only CRM with an AI concierge that reads your contact timeline and drafts emails in your voice."
                  testid="mktgen-differentiator"
                />

                {/* Honeypot */}
                <input
                  type="text"
                  name="company_website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.company_website}
                  onChange={(e) => setForm({ ...form, company_website: e.target.value })}
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                  aria-hidden="true"
                />

                {error && (
                  <div
                    className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm"
                    data-testid="mktgen-error"
                  >
                    <Warning size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full bg-[#0F0F1A] hover:bg-black text-white font-bold px-6 py-3.5 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 text-base"
                  data-testid="mktgen-submit-btn"
                >
                  {loading ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Crunchy is writing…
                    </>
                  ) : (
                    <>
                      <Sparkle size={18} weight="fill" className="text-[#F39C12]" />
                      Generate my marketing pack
                    </>
                  )}
                </button>

                <p className="text-xs text-[#9CA3AF] text-center">
                  Takes ~15 seconds. All 6 assets appear right below.
                </p>
              </div>
            </form>
          )}

          {result?.assets && (
            <div className="space-y-6" data-testid="mktgen-results">
              <div className="flex items-center justify-between flex-wrap gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-5 py-4">
                <div className="flex items-center gap-2">
                  <CheckCircle size={22} weight="fill" className="text-emerald-600" />
                  <div>
                    <div className="font-bold text-emerald-900">
                      Marketing pack ready for {result.product_name}
                    </div>
                    <div className="text-xs text-emerald-700">
                      6 assets · Copy any block or download the whole pack as markdown
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={downloadAll}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-2 rounded inline-flex items-center gap-1.5"
                    data-testid="mktgen-download-all-btn"
                  >
                    <Download size={13} weight="bold" />
                    Download .md
                  </button>
                  <button
                    type="button"
                    onClick={() => { setResult(null); setError(null); }}
                    className="bg-white hover:bg-gray-50 text-[#0F0F1A] border border-gray-300 text-xs font-bold px-3 py-2 rounded"
                    data-testid="mktgen-restart-btn"
                  >
                    Start over
                  </button>
                </div>
              </div>

              <AssetCard title="Landing page hero" testid="mktgen-asset-hero">
                <div className="space-y-1 text-sm">
                  <div><strong className="text-xs uppercase tracking-widest text-[#F39C12]">Eyebrow:</strong> {result.assets.hero?.eyebrow}</div>
                  <div className="text-lg font-black leading-tight mt-1">{result.assets.hero?.headline}</div>
                  <div className="text-[#4B5563]">{result.assets.hero?.subheadline}</div>
                  <div className="pt-2 flex flex-wrap gap-2">
                    <span className="bg-[#0F0F1A] text-white text-xs font-bold px-3 py-1 rounded">{result.assets.hero?.cta_primary}</span>
                    <span className="text-xs text-[#6B7280] font-semibold py-1">{result.assets.hero?.cta_secondary}</span>
                  </div>
                </div>
                <CopyBtn
                  text={`${result.assets.hero?.eyebrow}\n${result.assets.hero?.headline}\n${result.assets.hero?.subheadline}\nPrimary CTA: ${result.assets.hero?.cta_primary}\nSecondary CTA: ${result.assets.hero?.cta_secondary}`}
                  label="Hero"
                  onCopy={copyToClipboard}
                />
              </AssetCard>

              <AssetCard title="Email subject lines" testid="mktgen-asset-emails">
                <ul className="space-y-1.5 text-sm">
                  {(result.assets.email_subject_lines || []).map((s, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#9CA3AF] font-mono text-xs mt-1">{i + 1}.</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
                <CopyBtn
                  text={(result.assets.email_subject_lines || []).join('\n')}
                  label="Subject lines"
                  onCopy={copyToClipboard}
                />
              </AssetCard>

              <AssetCard title="Twitter / X thread" testid="mktgen-asset-tweets">
                <ol className="space-y-2 text-sm">
                  {(result.assets.tweet_thread || []).map((t, i) => (
                    <li key={i} className="border-l-2 border-[#F39C12] pl-3">
                      <div className="text-[10px] text-[#9CA3AF] font-mono mb-0.5">{i + 1}/{result.assets.tweet_thread.length}</div>
                      <div>{t}</div>
                    </li>
                  ))}
                </ol>
                <CopyBtn
                  text={(result.assets.tweet_thread || []).map((t, i) => `${i + 1}/${result.assets.tweet_thread.length} ${t}`).join('\n\n')}
                  label="Tweet thread"
                  onCopy={copyToClipboard}
                />
              </AssetCard>

              <AssetCard title="LinkedIn post" testid="mktgen-asset-linkedin">
                <div className="text-sm text-[#374151] whitespace-pre-line leading-relaxed">
                  {result.assets.linkedin_post}
                </div>
                <CopyBtn
                  text={result.assets.linkedin_post || ''}
                  label="LinkedIn post"
                  onCopy={copyToClipboard}
                />
              </AssetCard>

              <AssetCard title="Meta ad variations" testid="mktgen-asset-ads">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {(result.assets.meta_ads || []).map((ad, i) => (
                    <div key={i} className="border border-black/10 rounded p-3 bg-gray-50">
                      <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF] mb-1">Ad {i + 1}</div>
                      <div className="text-sm font-bold mb-1">{ad.headline}</div>
                      <div className="text-xs text-[#4B5563]">{ad.primary_text}</div>
                    </div>
                  ))}
                </div>
                <CopyBtn
                  text={(result.assets.meta_ads || []).map((ad, i) => `Ad ${i + 1}\nHeadline: ${ad.headline}\nBody: ${ad.primary_text}`).join('\n\n')}
                  label="Meta ads"
                  onCopy={copyToClipboard}
                />
              </AssetCard>

              <AssetCard title="30-second video script" testid="mktgen-asset-video">
                <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-wrap font-mono leading-relaxed text-[#374151]">
                  {result.assets.video_script_30s}
                </pre>
                <CopyBtn
                  text={result.assets.video_script_30s || ''}
                  label="Video script"
                  onCopy={copyToClipboard}
                />
              </AssetCard>

              {/* Conversion nudge */}
              <div className="bg-[#0F0F1A] text-white rounded-lg p-6 sm:p-8 text-center mt-8">
                <h3 className="text-2xl font-black mb-2">Impressed by Crunchy?</h3>
                <p className="text-white/70 text-sm mb-5 max-w-lg mx-auto">
                  This is a fraction of what Crunchy does inside SalesCrunchCRM.
                  He also reads your contact timeline, drafts emails in your
                  voice, checks your schedule, and finds the deals you&rsquo;re about
                  to lose. Try the full CRM free for 14 days.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 bg-[#F39C12] hover:bg-[#E08C00] text-black font-bold px-6 py-3 rounded"
                  data-testid="mktgen-signup-cta"
                >
                  Start my 14-day free trial
                  <ArrowRight size={16} weight="bold" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      <footer className="border-t border-black/5 py-6 px-6 text-center text-xs text-[#9CA3AF]">
        Marketing Generator · Powered by Crunchy · a{' '}
        <Link to="/" className="hover:text-black">SalesCrunchCRM</Link> product
      </footer>
    </div>
  );
}

// ---- small local building blocks ------------------------------------

function Field({ label, hint, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-widest text-[#0F0F1A] mb-1.5">
        {label}
        {hint && <span className="ml-2 font-normal normal-case tracking-normal text-[#9CA3AF]">— {hint}</span>}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded px-3 py-2.5 text-sm focus:border-[#F39C12] focus:outline-none focus:ring-1 focus:ring-[#F39C12]/30"
        data-testid={testid}
      />
    </div>
  );
}

function TextareaField({ label, hint, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-widest text-[#0F0F1A] mb-1.5">
        {label}
        {hint && <span className="ml-2 font-normal normal-case tracking-normal text-[#9CA3AF]">— {hint}</span>}
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={2}
        className="w-full border border-gray-300 rounded px-3 py-2.5 text-sm focus:border-[#F39C12] focus:outline-none focus:ring-1 focus:ring-[#F39C12]/30 resize-none"
        data-testid={testid}
      />
    </div>
  );
}

function AssetCard({ title, children, testid }) {
  return (
    <div
      className="bg-white border border-black/10 rounded-lg p-5 sm:p-6 shadow-sm"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-bold uppercase tracking-widest text-[#0F0F1A]">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function CopyBtn({ text, label, onCopy }) {
  return (
    <button
      type="button"
      onClick={() => onCopy(text, label)}
      className="mt-4 text-xs text-[#6B7280] hover:text-black inline-flex items-center gap-1"
    >
      <Copy size={12} weight="bold" />
      Copy {label.toLowerCase()}
    </button>
  );
}
